import { useState, useEffect } from 'react';
import { UserCog, Plus, Edit, Ban, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const AdminUsersManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'finance_admin'
  });

  useEffect(() => {
    fetchAdmins();
    fetchRoles();
  }, []);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/admins', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Failed to fetch admin users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/roles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/admins', formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Admin user created successfully');
      setShowCreateModal(false);
      setFormData({ email: '', name: '', password: '', role: 'finance_admin' });
      fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(`/admin/admins/${selectedAdmin.admin_id}`, {
        name: formData.name,
        role: formData.role,
        is_active: formData.is_active
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Admin user updated successfully');
      setShowEditModal(false);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin:', error);
      toast.error('Failed to update admin');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'super_admin': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'finance_admin': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'support_admin': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'trading_admin': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Admin Users Management</h1>
          <p className="text-gray-400 text-sm">Manage admin accounts and permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold text-sm"
        >
          <Plus className="h-4 w-4" />
          <span>Create Admin</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Admin</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Created</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {admins.map((admin) => (
                <tr key={admin.admin_id} className="hover:bg-gray-800/50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-white font-semibold text-sm">{admin.name}</p>
                      <p className="text-gray-400 text-xs">{admin.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeColor(admin.role)}`}>
                      {roles[admin.role]?.name || admin.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      admin.is_active 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-gray-400 text-sm">
                      {new Date(admin.created_at).toLocaleDateString()}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          setSelectedAdmin(admin);
                          setFormData({
                            name: admin.name,
                            role: admin.role,
                            is_active: admin.is_active
                          });
                          setShowEditModal(true);
                        }}
                        className="p-2 hover:bg-gray-700 rounded-lg text-blue-400"
                        title="Edit Admin"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminUsersManagement;
